import { TriangleAlert } from 'lucide-react-native';

import type { UseDraftPhase } from '../../hooks/useDraftHandOff';
import { REPLY_DRAFT_COPY } from '../copy';
import { DraftFooter } from '../screen/DraftFooter';

import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { getAccessibilityProps } from '@/util/accessibilityProps';

function failureMessage(phase: UseDraftPhase): string | null {
  if (phase.kind !== 'failed') {
    return null;
  }

  if (phase.error === 'timeout' || phase.error === 'empty_reply') {
    return REPLY_DRAFT_COPY.useFailed[phase.error];
  }

  return REPLY_DRAFT_COPY.useFailed.other;
}

/**
 * "Write my reply", with what still needs fixing said above it. The must-fix count
 * is shown before the button is pressed, so the expert learns what will hold
 * the hand-off while still beside the suggestions, not after the check runs.
 * While the server checks the reply the button says so, cannot be pressed
 * again, and the change is announced. A point typed but not added, which
 * "Write my reply" adds first, counts as checking; "Add point" on its own
 * does not, since the expert has not asked for the reply yet. The one primary action on the screen,
 * so the only brand-filled control.
 */
export function UseDraftFooter({
  phase,
  isAddingNote = false,
  mustFixRemaining,
  onUse,
  onBack,
}: {
  phase: UseDraftPhase;
  /** True only while "Write my reply" adds a typed point before writing. */
  isAddingNote?: boolean;
  mustFixRemaining: number;
  onUse: () => void;
  /** Hidden while the check runs, so the expert cannot leave a check half run. */
  onBack?: () => void;
}) {
  const isChecking = isAddingNote || phase.kind === 'checking';

  const failure = failureMessage(phase);

  const label = isChecking ? REPLY_DRAFT_COPY.checking : REPLY_DRAFT_COPY.useDraft;

  return (
    <DraftFooter
      onBack={isChecking ? undefined : onBack}
      notice={
        <>
          {mustFixRemaining > 0 ? (
            <HStack className="items-center gap-2" testID="reply-draft-must-fix-remaining">
              <Icon as={TriangleAlert} size="sm" className="text-error-700" />
              <Text size="sm" className="font-medium text-error-700">
                {REPLY_DRAFT_COPY.mustFixRemaining(mustFixRemaining)}
              </Text>
            </HStack>
          ) : null}

          {failure ? (
            <Text
              size="sm"
              className="text-error-700"
              testID="reply-draft-use-failed"
              accessibilityLiveRegion="polite"
              role="alert">
              {failure}
            </Text>
          ) : null}
        </>
      }>
      <Button
        size="xl"
        action="primary"
        className="min-h-12 w-full"
        isDisabled={isChecking}
        onPress={onUse}
        testID="reply-draft-use"
        accessibilityLiveRegion="polite"
        {...getAccessibilityProps({ role: 'button', label, hint: REPLY_DRAFT_COPY.useDraftHint })}
        accessibilityState={{ busy: isChecking, disabled: isChecking }}>
        {isChecking ? <ButtonSpinner /> : null}
        <ButtonText>{label}</ButtonText>
      </Button>
    </DraftFooter>
  );
}
